import { DecimalPipe } from '@angular/common';
import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { forkJoin } from 'rxjs';

import { toErrorMessage } from '../../../helpers/http-error';
import { AnalyticsService, isUnresolvedScope } from '../../../services/analytics.service';
import { LocaleService } from '../../../services/locale.service';
import {
  PeriodPreset,
  SeriesPoint,
  VehicleSummary,
  resolvePeriod,
} from '../../../types/analytics.types';
import { TPipe } from '../../../pipe/t.pipe';
import {
  PrettySelectComponent,
  PrettySelectOption,
} from '../../ui/pretty-select/pretty-select.component';
import { AnalyticsDonutComponent } from '../analytics-donut/analytics-donut.component';
import { AnalyticsSeriesChartComponent } from '../analytics-series-chart/analytics-series-chart.component';

/**
 * Section analytics **Parc** — **partagée à l'identique** entre les 3 dashboards.
 * Endpoints `vehicles/summary` + `vehicles/series` : **même URL**, le back dérive
 * le périmètre du JWT (admin=plateforme, owner=son parc, operator=ses prestataires).
 *
 * ⚠️ **Deux temporalités mêlées, à libeller clairement** (§11) : le **parc**
 * (`total`/`active`/`underReview`/`inactive`/`byStatus`) est à **l'instant t**
 * (non borné par la période) ; seul **`newVehicles`** + la série d'acquisition
 * couvre `[from, to]`.
 *
 * Garde-fous : 403 = acteur non résolvable → message dédié
 * ({@link isUnresolvedScope}). `byStatus.value` vaut **toujours 0** (comptage, pas
 * un montant) → donut et tri sur **`count`** (`metric="count"`).
 *
 * ⚠️ Post-déploiement : le scope OPERATOR du parc démarre **au flux live** (le
 * backfill ne résout pas l'opérateur par véhicule) ; PLATFORM + OWNER complets
 * dès le backfill. Cf. `docs/ANALYTICS_STATUS.md` §11.
 */
@Component({
  selector: 'app-vehicles-analytics',
  standalone: true,
  imports: [
    DecimalPipe,
    FormsModule,
    TPipe,
    PrettySelectComponent,
    AnalyticsSeriesChartComponent,
    AnalyticsDonutComponent,
  ],
  template: `
    <section class="rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03] mb-6">
      <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 class="text-base font-semibold text-gray-800 dark:text-white/90">{{ 'analytics.vehicles.title' | t }}</h3>
          <p class="mt-0.5 text-sm text-gray-500 dark:text-gray-400">{{ 'analytics.vehicles.subtitle' | t }}</p>
        </div>
        <div class="w-full sm:w-44">
          <app-pretty-select
            [options]="periodOptions"
            [ngModel]="period()"
            (ngModelChange)="changePeriod($event)"
          />
        </div>
      </div>

      @if (loading()) {
        <div class="flex h-[220px] items-center justify-center text-sm text-gray-400 dark:text-gray-500">
          {{ 'analytics.loading' | t }}
        </div>
      } @else if (unresolved()) {
        <div class="mt-4 flex items-start gap-3 rounded-xl border border-blue-light-200 bg-blue-light-50 p-4 dark:border-blue-light-500/30 dark:bg-blue-light-500/10">
          <svg class="mt-0.5 shrink-0 text-blue-light-500" width="20" height="20" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.8"/><path d="M12 8h.01M11 12h1v4h1" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
          <p class="text-sm text-blue-light-700 dark:text-blue-light-200">{{ 'analytics.unresolved' | t }}</p>
        </div>
      } @else if (error()) {
        <div class="mt-4 flex items-center justify-between gap-3 rounded-xl border border-error-200 bg-error-50 p-4 dark:border-error-500/30 dark:bg-error-500/10">
          <p class="text-sm text-error-700 dark:text-error-400">{{ error() }}</p>
          <button type="button" (click)="load()" class="shrink-0 text-xs font-medium text-error-700 hover:underline dark:text-error-400">{{ 'common.retry' | t }}</button>
        </div>
      } @else if (summary(); as r) {
        <!-- KPIs parc (à l'instant t) -->
        <div class="mt-5 grid grid-cols-2 gap-4 lg:grid-cols-4">
          <div class="rounded-xl border border-gray-100 p-4 dark:border-gray-800">
            <span class="text-xs font-medium text-gray-500 dark:text-gray-400">{{ 'analytics.vehicles.total' | t }}</span>
            <p class="mt-1 text-xl font-bold text-gray-800 dark:text-white/90">{{ r.totalVehicles | number }}</p>
            <p class="mt-0.5 text-xs text-gray-400">{{ 'analytics.vehicles.instantT' | t }}</p>
          </div>
          <div class="rounded-xl border border-gray-100 p-4 dark:border-gray-800">
            <span class="text-xs font-medium text-gray-500 dark:text-gray-400">{{ 'analytics.vehicles.active' | t }}</span>
            <p class="mt-1 text-xl font-bold text-brand-700 dark:text-brand-400">{{ r.activeVehicles | number }}</p>
          </div>
          <div class="rounded-xl border border-gray-100 p-4 dark:border-gray-800">
            <span class="text-xs font-medium text-gray-500 dark:text-gray-400">{{ 'analytics.vehicles.underReview' | t }}</span>
            <p class="mt-1 text-xl font-bold text-gray-800 dark:text-white/90">{{ r.underReviewVehicles | number }}</p>
          </div>
          <div class="rounded-xl border border-gray-100 p-4 dark:border-gray-800">
            <span class="text-xs font-medium text-gray-500 dark:text-gray-400">{{ 'analytics.vehicles.inactive' | t }}</span>
            <p class="mt-1 text-xl font-bold text-gray-800 dark:text-white/90">{{ r.inactiveVehicles | number }}</p>
          </div>
        </div>

        <!-- Nouveaux véhicules sur la période (temporalité distincte du parc) -->
        <div class="mt-4 inline-flex items-center gap-2 rounded-lg bg-brand-50 px-3 py-1.5 text-sm text-brand-800 dark:bg-brand-500/10 dark:text-brand-300">
          <span>{{ 'analytics.vehicles.newOverPeriod' | t }} :</span>
          <strong>{{ r.newVehicles | number }}</strong>
        </div>

        <!-- Courbe acquisition + donut répartition -->
        <div class="mt-6 grid gap-6 lg:grid-cols-3">
          <div class="lg:col-span-2">
            <h4 class="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">{{ 'analytics.vehicles.chartTitle' | t }}</h4>
            <app-analytics-series-chart
              [points]="series()"
              metric="count"
              [seriesName]="seriesName()"
              [emptyLabel]="'analytics.empty' | t"
            />
          </div>
          <div>
            <h4 class="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">{{ 'analytics.vehicles.byStatusTitle' | t }}</h4>
            <app-analytics-donut
              [items]="r.byStatus"
              metric="count"
              keyPrefix="analytics.vehicles.status"
              [emptyLabel]="'analytics.empty' | t"
            />
          </div>
        </div>
      }
    </section>
  `,
})
export class VehiclesAnalyticsComponent implements OnInit {
  private readonly analytics = inject(AnalyticsService);
  private readonly locale = inject(LocaleService);
  private readonly destroyRef = inject(DestroyRef);

  readonly period = signal<PeriodPreset>('30d');
  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  /** `true` = acteur non résolvable (403) → message dédié, pas une erreur. */
  readonly unresolved = signal(false);

  readonly summary = signal<VehicleSummary | null>(null);
  readonly series = signal<SeriesPoint[]>([]);

  readonly periodOptions: PrettySelectOption<PeriodPreset>[] = [
    { value: '7d', label: 'analytics.period.7d' },
    { value: '30d', label: 'analytics.period.30d' },
    { value: '90d', label: 'analytics.period.90d' },
    { value: '365d', label: 'analytics.period.365d' },
  ];

  ngOnInit(): void {
    this.load();
  }

  changePeriod(p: PeriodPreset | null): void {
    if (!p || p === this.period()) return;
    this.period.set(p);
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.error.set(null);
    this.unresolved.set(false);
    const period = resolvePeriod(this.period());

    forkJoin({
      summary: this.analytics.vehicleSummary(period),
      series: this.analytics.vehicleSeries(period),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ summary, series }) => {
          // byStatus : trier sur `count` (value = 0) pour un donut lisible.
          summary.byStatus = [...(summary.byStatus ?? [])].sort((a, b) => b.count - a.count);
          this.summary.set(summary);
          this.series.set(series);
          this.loading.set(false);
        },
        error: (err: unknown) => {
          this.loading.set(false);
          if (isUnresolvedScope(err)) {
            this.unresolved.set(true);
          } else {
            this.error.set(toErrorMessage(err, 'errors.analyticsLoadFailed'));
          }
        },
      });
  }

  seriesName(): string {
    return this.locale.t('analytics.vehicles.seriesName');
  }
}
